// eslint-disable-next-line import/order
import {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Tags,
  NavCarousel,
  Button,
  SliderOver,
  NavBar,
  Modal,
} from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useGetCurrentTeamF11 } from '@/hooks/useMyTeam11';
import { useGetCurrentTeamF5 } from '@/hooks/useMyTeam5';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { useTeamStore } from '@/hooks/useTeamStore';
import { useTournament } from '@/hooks/useTournament';
import { TeamActionTypes } from '@/store';
import { EnumTeamContext, TemplatePlayer } from '@/types/team';
import {
  addAditionalZeroIfDecimal,
  cn,
  formatMillionBalance,
  resolvePlayerPrice,
  PLAYER_POSITION_BY_ID,
} from '@/utils';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  context?: EnumTeamContext;
};

export const ModalChangeSuggestion = ({
  isOpen,
  onClose,
  context = EnumTeamContext.Team11,
}: Props) => {
  const playerByTemplate = usePlayerTemplateMap();
  const { tournamentMap } = useTournament();
  const { isDesktop, isMobile } = useIsDesktop();
  const { dispatch } = useTeamStore();
  const { userInitialData } = useAuth();

  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedPlayer, setSelectedPlayer] = useState<TemplatePlayer>();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;

  const { data: team11Data } = useGetCurrentTeamF11(
    context === EnumTeamContext.Team11
  );
  const { data: team5Data } = useGetCurrentTeamF5(
    userInitialData?.tiene_equipo_f5,
    {
      enabled: context === EnumTeamContext.Team5,
    }
  );

  const getCurrentTeamData =
    context === EnumTeamContext.Team11 ? team11Data : team5Data;

  const suggestions = getCurrentTeamData?.estado
    ? getCurrentTeamData.arraySugerenciaTO || []
    : [];

  const scrollableDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTop = 0;
    }
  }, [activeIndex, scrollableDivRef]);

  // Styles
  const baseStyles = {
    container: 'flex flex-col items-center',
    playerCard: 'flex items-center',
    playerInfo: 'flex mt-3',
    playerName: 'text-gdt-gray font-semibold',
    listContainer: 'bg-white max-h-[280px] overflow-y-auto h-screen',
    button: {
      base: 'aspect-square h-4 w-4 rounded-full border flex items-center justify-center focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-gdt-blue hover:cursor-pointer',
      selected: 'bg-gdt-blue-xl2 border-gdt-blue',
      unselected: 'border-gdt-gray-4 bg-white',
    },
    selectedDot: 'aspect-square rounded-full bg-gdt-blue w-2 h-2',
    divider:
      "after:content-[''] relative after:block after:h-px after:w-full after:border-b after:border-D2D6D6-500 after:absolute after:bottom-0 after:left-0",
    text: {
      title: 'text-base font-bold',
      subtitle: 'text-base font-bold',
      price: 'text-sm lg:text-base text-gdt-gray-3 font-semibold',
      points: 'text-base text-gdt-gray-3 font-semibold',
      team: 'text-gdt-gray-3 font-medium uppercase text-xs md:text-sm',
    },
  };

  const changeActiveIndex = useCallback((index: SetStateAction<number>) => {
    setActiveIndex(index);
    setSelectedPlayer(undefined);
  }, []);

  const getPlayersByIds = (playerIds: number[]) => {
    return playerIds.map((id) => playerByTemplate.get(id));
  };

  const handlePlayerSelect = (player: TemplatePlayer) => {
    setSelectedPlayer(player);
  };

  const handleSubmit = () => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        selectedPlayerOnTeam: playerByTemplate.get(
          suggestions[activeIndex].id
        ) as TemplatePlayer,
        selectedPlayerOnList: selectedPlayer as TemplatePlayer,
        addToTeamBySuggestionModal: true,
      },
    });
    onClose();
  };

  const suggestionTitle = () => {
    const hasGrupo1 = suggestions.some((jugador) => jugador.idGrupo === 1);
    const hasGrupo2 = suggestions.some((jugador) => jugador.idGrupo === 2);
    const totalJugadores = suggestions.length;

    if (hasGrupo1 && totalJugadores > 1) {
      return 'Estos jugadores no jugarían la próxima fecha:';
    }
    if (totalJugadores === 1 && hasGrupo1) {
      return 'Este jugador no jugaría la próxima fecha:';
    }
    if (!hasGrupo1 && hasGrupo2) {
      return totalJugadores > 1
        ? 'Estos jugadores en las últimas 3 fechas sumaron menos de 5 puntos:'
        : 'Este jugador en las últimas 3 fechas sumó menos de 5 puntos:';
    }
    return '';
  };

  const carouselPlayer = useCallback(() => {
    const hasGrupo1 = suggestions.some(
      (suggestion) => suggestion.idGrupo === 1
    );

    const filteredSuggestions = hasGrupo1
      ? suggestions.filter((suggestion) => suggestion.idGrupo === 1)
      : suggestions.filter((suggestion) => suggestion.idGrupo === 2);

    const players = getPlayersByIds(
      filteredSuggestions.map((suggestion) => suggestion.id)
    );

    return players.map((player) => {
      const statusInTournament =
        player && tournamentStatusMap
          ? tournamentStatusMap.get(player.e)
          : null;

      const playerPosition = player?.p
        ? PLAYER_POSITION_BY_ID[player.p as keyof typeof PLAYER_POSITION_BY_ID]
        : null;

      const playerProm = player ? addAditionalZeroIfDecimal(player.pm) : null;

      return (
        <div key={player?.id} className="flex flex-col items-center my-6">
          <div className="flex items-center">
            <p className="text-gdt-gray font-bold text-[16px]">
              {player?.a}, {player?.n}
            </p>
          </div>
          <div className="flex mt-3">
            {statusInTournament && (
              <Tags.Alert
                isUppercase
                statusId={statusInTournament?.id}
                textClassName="text-[10px] leading-4"
              >
                {!hasGrupo1
                  ? `${playerPosition} - ${playerProm} PROM.`
                  : `${playerPosition} - ${statusInTournament.nombre}`}
              </Tags.Alert>
            )}
          </div>
        </div>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPlayersByIds, suggestions, tournamentStatusMap, isDesktop, isMobile]);

  const DesktopPlayerRow = ({
    player,
    isSelected,
  }: {
    player: TemplatePlayer;
    isSelected: boolean;
  }) => {
    const statusInTournament =
      player && tournamentStatusMap ? tournamentStatusMap.get(player.e) : null;
    const team = player && teamsMap ? teamsMap.get(player?.c) : null;

    return (
      <div
        aria-checked={isSelected}
        className={cn(
          'flex items-center gap-x-4 px-5 py-4 h-[70px] cursor-pointer',
          baseStyles.divider,
          isSelected ? 'bg-gray-100' : ''
        )}
        role="radio"
        tabIndex={0}
        onClick={() => handlePlayerSelect(player)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handlePlayerSelect(player);
          }
        }}
      >
        <div className="w-[15px]">
          <div
            className={cn(
              baseStyles.button.base,
              isSelected
                ? baseStyles.button.selected
                : baseStyles.button.unselected
            )}
          >
            {isSelected && <div className={baseStyles.selectedDot} />}
          </div>
        </div>
        <div className="w-[325px] flex items-center gap-x-3">
          <p className="text-gdt-gray font-bold text-xs w-[125px]">
            {player?.a}, {player?.n}
          </p>
          <p className="text-gdt-gray-3 font-medium uppercase text-[10px]">
            {team?.nombre}
          </p>
        </div>
        <div className="flex items-center gap-x-3">
          <div className="w-[70px]">
            {statusInTournament && (
              <Tags.Alert
                isUppercase
                statusId={statusInTournament?.id}
                textClassName="truncate text-[10px] leading-4"
              >
                {statusInTournament.nombre}
              </Tags.Alert>
            )}
          </div>
          <div className="w-[100px] text-right">
            <span className="text-[11px]  text-gdt-gray-3 font-semibold">
              ${formatMillionBalance(resolvePlayerPrice(player?.cot))}
            </span>
          </div>
          <div className="w-[35px] text-right text-[12px]">
            <span className="text-sm  text-gdt-gray-3 font-semibold">
              {player && addAditionalZeroIfDecimal(player.pm)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const MobilePlayerRow = ({
    player,
    isSelected,
  }: {
    player: TemplatePlayer;
    isSelected: boolean;
  }) => {
    const statusInTournament =
      player && tournamentStatusMap ? tournamentStatusMap.get(player.e) : null;
    const team = player && teamsMap ? teamsMap.get(player?.c) : null;

    return (
      <div
        aria-checked={isSelected}
        className={cn(
          'flex justify-between items-start pl-[20px] pr-[23px] py-[16px] bg-white h-[90px] cursor-pointer',
          baseStyles.divider,
          isSelected ? 'bg-gray-100' : ''
        )}
        role="radio"
        tabIndex={0}
        onClick={() => handlePlayerSelect(player)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handlePlayerSelect(player);
          }
        }}
      >
        <div className="flex items-center">
          <div
            className={cn(
              `${baseStyles.button.base} mr-8`,
              isSelected
                ? baseStyles.button.selected
                : baseStyles.button.unselected
            )}
          >
            {isSelected && <div className={baseStyles.selectedDot} />}
          </div>
          <div className="flex flex-col">
            <p className="text-[12px] font-bold text-gdt-gray flex items-center">
              {player?.a}, {player?.n}
            </p>
            <p className="text-gdt-gray-3 font-medium uppercase text-[10px] md:text-sm">
              {team?.nombre}
            </p>
            <div className="flex gap-4">
              <span className="text-[11px] lg:text-base text-gdt-gray-3 font-semibold">
                ${formatMillionBalance(resolvePlayerPrice(player?.cot))}
              </span>
              {statusInTournament && (
                <Tags.Alert
                  isUppercase
                  statusId={statusInTournament?.id}
                  textClassName="text-[10px] truncate leading-4"
                >
                  {statusInTournament.nombre}
                </Tags.Alert>
              )}
            </div>
          </div>
        </div>
        <div>
          <span className="text-[12px] text-gdt-gray-3 font-semibold">
            {player && addAditionalZeroIfDecimal(player.pm)}
          </span>
        </div>
      </div>
    );
  };

  if (isDesktop && !isMobile) {
    return (
      <Modal
        className="bg-gdt-gray-7 py-8 px-0 w-auto"
        controlledOnClose={onClose}
        isControlledOpen={isOpen}
        render={() => (
          <div className="max-w-[600px] w-full px-8 overflow-y-auto flex flex-col">
            <div className="my-6">
              <NavCarousel IndexHandler={changeActiveIndex}>
                {carouselPlayer()}
              </NavCarousel>
            </div>
            <div className={baseStyles.container}>
              <p className="text-[14px] font-bold">Sugerencias de reemplazo</p>
            </div>
            <div className="mb-4 flex justify-end">
              <p className="text-gdt-gray-3 font-light text-[10px]">
                Puntos Prom.
              </p>
            </div>
            <div ref={scrollableDivRef} className={baseStyles.listContainer}>
              {suggestions[activeIndex].mejores_posibles.map((id) => {
                const player = playerByTemplate.get(id);
                return player ? (
                  <DesktopPlayerRow
                    key={player.id}
                    isSelected={selectedPlayer?.id === player.id}
                    player={player}
                  />
                ) : null;
              })}
            </div>
            <div className={`${baseStyles.container} mt-9`}>
              <Button
                className="text-[14px] font-bold px-20 py-4"
                disabled={!selectedPlayer}
                type="button"
                variant="primary"
                onClick={handleSubmit}
              >
                Reemplazar jugador
              </Button>
            </div>
          </div>
        )}
        title={suggestionTitle()}
        titleClassName="text-[14px] font-bold"
        variant="controlled"
      />
    );
  }

  if (isMobile && !isDesktop) {
    return (
      <SliderOver open={isOpen}>
        <NavBar
          mobile={{
            leftContent: {
              backAction: onClose,
            },
            centerContent: {
              title: 'Sugerencias',
            },
          }}
        >
          <div className="bg-gdt-gray-7">
            <div className={`${baseStyles.container} mt-4 px-4`}>
              <p className="text-[14px] font-bold">{suggestionTitle()}</p>
            </div>
            <div className="my-6">
              <NavCarousel IndexHandler={changeActiveIndex}>
                {carouselPlayer()}
              </NavCarousel>
            </div>
            <div className="flex flex-col lg:px-10 relative">
              <div className="items-center flex justify-between pr-[23px] lg:pr-[10px] min-h-[40px] border-b border-gdt-gray-7 pl-[16px] sticky top-[51px]">
                <p className="text-[14px] font-bold">
                  Sugerencias de reemplazo
                </p>
                <p className="text-gdt-gray-3 font-light text-[11px]">
                  Ptos. Prom.
                </p>
              </div>
              <div className="flex-1 overflow-auto pb-[92px]">
                {suggestions[activeIndex].mejores_posibles.map((id) => {
                  const player = playerByTemplate.get(id);
                  return player ? (
                    <MobilePlayerRow
                      key={player.id}
                      isSelected={selectedPlayer?.id === player.id}
                      player={player}
                    />
                  ) : null;
                })}
              </div>
              <div className="px-4 pb-6 pt-6 lg:text-center fixed bottom-0 left-0 w-full bg-white">
                <Button
                  className="w-full lg:w-60"
                  disabled={!selectedPlayer}
                  type="button"
                  variant="primary"
                  onClick={handleSubmit}
                >
                  Reemplazar jugador
                </Button>
              </div>
            </div>
          </div>
        </NavBar>
      </SliderOver>
    );
  }
};
